// pothos manager «Статистика по дням» — /api/managerDailyStats (issue #582):
// дневная сводка для кабинета менеджера — Collector (расширение), QA и
// скрейперы. День = календарный день America/Los_Angeles, как и остальные
// дневные метрики кабинета (см. apps/api/src/platform/dates.ts); поле tz в
// ответе — явный маркер этого соглашения для клиента.
import { z } from 'zod';

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

// Диапазон LA-дней включительно, не шире 31 дня; без параметров воркер
// отдаёт последние 7 дней (по «сегодня» менеджера в LA).
export const PothosManagerDailyStatsQuerySchema = z.object({
  from: z.string().regex(DAY_RE).optional(),
  to: z.string().regex(DAY_RE).optional(),
});
export type PothosManagerDailyStatsQuery = z.infer<typeof PothosManagerDailyStatsQuerySchema>;

// Строка сотрудника с расширением (pothos_ext_events, label людской —
// NOT LIKE 'scraper%'). accepted/rejected — вердикт авто-приёмки по кликам
// ЭТОГО дня (атрибуция к дню клика, не к дню вердикта: менеджер смотрит
// «что дала дневная работа»).
export const PothosDailyCollectorRowSchema = z.object({
  label: z.string(),
  // Имя, заданное менеджером за токеном (pothos_ext_tokens.display_name);
  // пустая строка — имени нет, витрина показывает label.
  display_name: z.string(),
  clicked: z.number(),
  uploaded: z.number(),   // status='uploaded' — файл доехал до R2
  url_only: z.number(),   // status='url_only' — только ссылка
  accepted: z.number(),   // eval_verdict='accepted'
  rejected: z.number(),   // eval_verdict='rejected'
  first_at: z.string(),   // ISO первого события LA-дня
  last_at: z.string(),    // ISO последнего события LA-дня
  // Серверная оценка активного времени тем же правилом, что счётчик в
  // расширении: сумма min(gap, 90с) по последовательным received_at.
  active_seconds: z.number(),
});
export type PothosDailyCollectorRow = z.infer<typeof PothosDailyCollectorRowSchema>;

// Вердикты QA за день (pothos_assets по checked_at). Атрибуции «кто
// проверил» по людям здесь нет — только тоталы. split — кадры-нарезки
// (split_from<>''), созданные в этот день.
export const PothosDailyQaSchema = z.object({
  accepted: z.number(),
  rejected: z.number(),
  split: z.number(),
});
export type PothosDailyQa = z.infer<typeof PothosDailyQaSchema>;

// Тоталы скрейперов (label LIKE 'scraper%') одним объектом.
export const PothosDailyScrapeSchema = z.object({
  clicked: z.number(),
  accepted: z.number(),
});
export type PothosDailyScrape = z.infer<typeof PothosDailyScrapeSchema>;

export const PothosManagerDailyStatsDaySchema = z.object({
  date: z.string(),                                  // YYYY-MM-DD (LA-день)
  collector: z.array(PothosDailyCollectorRowSchema), // отсортировано по label
  qa: PothosDailyQaSchema,
  scrape: PothosDailyScrapeSchema,
});
export type PothosManagerDailyStatsDay = z.infer<typeof PothosManagerDailyStatsDaySchema>;

export const PothosManagerDailyStatsResSchema = z.object({
  ok: z.literal(true),
  from: z.string(),
  to: z.string(),
  tz: z.string(), // 'America/Los_Angeles' — таймзона дневных границ
  // Все дни диапазона по возрастанию, включая пустые (нули) — клиент
  // листает по дням без дыр.
  days: z.array(PothosManagerDailyStatsDaySchema),
});
export type PothosManagerDailyStatsRes = z.infer<typeof PothosManagerDailyStatsResSchema>;


// ── POST /api/managerTokenName — задать/сменить имя сборщика за токеном ──────
// Имя живёт при токене (pothos_ext_tokens), label остаётся техническим ключом.
export const PothosManagerTokenNameReqSchema = z.object({
  label: z.string().min(1).max(40),
  display_name: z.string().max(60), // пустая строка = снять имя
});
export type PothosManagerTokenNameReq = z.infer<typeof PothosManagerTokenNameReqSchema>;

export const PothosManagerTokenNameResSchema = z.object({
  ok: z.boolean(),
  label: z.string(),
  display_name: z.string(),
});
export type PothosManagerTokenNameRes = z.infer<typeof PothosManagerTokenNameResSchema>;
